
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import { apiRequest } from '../../fetchSetup';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  Person
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Helper: small red asterisk label ────────────────────────────────────────
const Req = () => (
  <Typography component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Typography>
);

const DistributorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const DRAWER_WIDTH = '280px';
  const today = new Date().toISOString().split('T')[0];

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [medicineSearchQuery, setMedicineSearchQuery] = useState('');
  const [medicineSuggestions, setMedicineSuggestions] = useState([]);
  const [loadingMedicines, setLoadingMedicines] = useState(false);

  const [contactPersons, setContactPersons] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [medicineList, setMedicineList] = useState([]);

  const [errors, setErrors] = useState({ lat: '', lng: '', pincode: '' });

  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    distributor_type: 'distributor',
    distributor_code: '',
    phone: '',
    alternate_phone: '',
    email: '',
    website: '',
    address: { street: '', city: '', state: '', pincode: '', country: 'India' },
    warehouse_address: { street: '', city: '', state: '', pincode: '', country: 'India' },
    gst_number: '',
    pan_number: '',
    drug_license_number: '',
    drug_license_expiry: '',
    fssai_number: '',
    bank_details: {
      bank_name: '', account_name: '', account_number: '',
      ifsc_code: '', branch: '', upi_id: ''
    },
    delivery_metrics: { on_time_percentage: 100, avg_delay_days: 0, total_deliveries: 0 },
    rating: {
      delivery_rating: 5, quality_rating: 5, price_rating: 5,
      service_rating: 5, overall_rating: 5, total_orders: 0,
      on_time_deliveries: 0, return_rate: 0, average: 0, total_reviews: 0
    },
    location: { lat: '', lng: '', city: '', state: '', pincode: '' },
    shipping: { per_km_cost: 0, base_cost: 0, free_shipping_above: 0, avg_delivery_days: 1 },
    payment_terms: 'credit_30_days',
    credit_limit: 0,
    credit_period_days: 30,
    discount_percentage: 0,
    minimum_order_value: 0,
    delivery_days: 3,
    product_categories: [],
    brands_supplied: [],
    is_active: true,
    is_verified: false,
    notes: '',
    terms_conditions: ''
  });

  useEffect(() => {
    if (isEditMode) fetchDistributor();
  }, [id]);

  const fetchDistributor = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`${process.env.REACT_APP_API}/distributors/${id}`);
      const data = await response.json();
      if (response.ok) {
        setFormData({
          ...data,
          address: data.address || formData.address,
          warehouse_address: data.warehouse_address || formData.warehouse_address,
          bank_details: data.bank_details || formData.bank_details,
          rating: data.rating || formData.rating,
          location: data.location || formData.location,
          shipping: data.shipping || formData.shipping,
          delivery_metrics: data.delivery_metrics || formData.delivery_metrics,
          product_categories: data.product_categories || [],
          brands_supplied: data.brands_supplied || []
        });
        setContactPersons(data.contact_persons || []);
        setMedicineList(data.distributor_medicine_ids || []);
      } else {
        showSnackbar('Failed to load distributor', 'error');
      }
    } catch (error) {
      showSnackbar('Error loading distributor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field, value) => {
    let error = '';
    if (field === 'lat') {
      const lat = parseFloat(value);
      if (value === '') error = 'Latitude is required';
      else if (isNaN(lat)) error = 'Latitude must be a number';
      else if (lat < -90 || lat > 90) error = 'Latitude must be between -90 and 90';
    }
    if (field === 'lng') {
      const lng = parseFloat(value);
      if (value === '') error = 'Longitude is required';
      else if (isNaN(lng)) error = 'Longitude must be a number';
      else if (lng < -180 || lng > 180) error = 'Longitude must be between -180 and 180';
    }
    if (field === 'pincode') {
      if (value && !/^[1-9][0-9]{5}$/.test(value)) error = 'Pincode must be 6 digits';
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleRemoveMedicine = (index) => setMedicineList(prev => prev.filter((_, i) => i !== index));

  const handleMedicineChange = (index, field, value) => {
    const updated = [...medicineList];
    updated[index][field] = value;
    setMedicineList(updated);
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleNestedChange = (parent, field, value) =>
    setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));

  const handleAddContactPerson = () =>
    setContactPersons([...contactPersons, {
      name: '', designation: '', phone: '', email: '',
      is_primary: contactPersons.length === 0
    }]);

  const handleRemoveContactPerson = (index) =>
    setContactPersons(contactPersons.filter((_, i) => i !== index));

  const handleContactPersonChange = (index, field, value) => {
    const updated = [...contactPersons];
    updated[index][field] = value;
    setContactPersons(updated);
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      setFormData(prev => ({
        ...prev,
        product_categories: [...prev.product_categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (category) =>
    setFormData(prev => ({
      ...prev,
      product_categories: prev.product_categories.filter(c => c !== category)
    }));

  const handleAddBrand = () => {
    if (newBrand.trim()) {
      setFormData(prev => ({
        ...prev,
        brands_supplied: [...prev.brands_supplied, newBrand.trim()]
      }));
      setNewBrand('');
    }
  };

  const handleRemoveBrand = (brand) =>
    setFormData(prev => ({
      ...prev,
      brands_supplied: prev.brands_supplied.filter(b => b !== brand)
    }));

  const handlePhoneChange = (field, value) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length <= 10) handleChange(field, value);
  };

  const fetchMedicineSuggestions = async (query) => {
    if (!query || query.length < 2) { setMedicineSuggestions([]); return; }
    try {
      setLoadingMedicines(true);
      const response = await apiRequest(
        `${process.env.REACT_APP_API}/medicine/search?query=${encodeURIComponent(query)}&limit=20`
      );
      const data = await response.json();
      setMedicineSuggestions(response.ok ? (data.medicines || []) : []);
    } catch {
      setMedicineSuggestions([]);
    } finally {
      setLoadingMedicines(false);
    }
  };


  const validateForm = () => {
    const errs = [];

    // 1. Distributor Name — MANDATORY
    if (!formData.name?.trim())
      errs.push('Distributor Name is required');

    // 2. Drug License Number — MANDATORY
    if (!formData.drug_license_number?.trim())
      errs.push('Drug License Number is required');

    // 3. GST Number — MANDATORY
    if (!formData.gst_number?.trim())
      errs.push('GST Number is required');

    // 4. Phone — MANDATORY, must be 10 digits
    if (!formData.phone?.trim())
      errs.push('Phone Number is required');
    else if (formData.phone.length !== 10)
      errs.push('Phone Number must be exactly 10 digits');

    // 5. Email — MANDATORY, must be valid format
    if (!formData.email?.trim())
      errs.push('Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errs.push('Please enter a valid Email address');

    // Everything else is OPTIONAL — no validation blocks

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showSnackbar(validationErrors.join(' | '), 'error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        contact_persons: contactPersons,
        distributor_medicine_ids: medicineList
      };
      const url = isEditMode
        ? `${process.env.REACT_APP_API}/distributors/${id}`
        : `${process.env.REACT_APP_API}/distributors`;
      const response = await apiRequest(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        showSnackbar(
          isEditMode ? 'Distributor updated successfully' : 'Distributor created successfully',
          'success'
        );
        setTimeout(() => navigate('/DistributorList'), 1500);
      } else {
        showSnackbar(data.error || 'Failed to save distributor', 'error');
      }
    } catch {
      showSnackbar('Error saving distributor', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Mandatory field note ─────────────────────────────────────────────────
  const MandatoryNote = () => (
    <Alert severity="info" sx={{ mb: 3, py: 0.5 }}>
      Fields marked with <Typography component="span" sx={{ color: 'error.main', fontWeight: 700 }}> * </Typography>
      are <strong>mandatory</strong>. All other fields are optional.
    </Alert>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', ml: { xs: 0, md: DRAWER_WIDTH } }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/DistributorList')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A' }}>
            {isEditMode ? 'Edit Distributor' : 'Add New Distributor'}
          </Typography>
        </Box>

        <MandatoryNote />

        <form onSubmit={handleSubmit}>

          {/* ── Basic Information ─────────────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>

              {/* Name — MANDATORY */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  // required
                  label={<>Distributor Name <Req /></>}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={formData.name !== undefined && formData.name.trim() === '' && formData.name !== ''}
                />
              </Grid>

              {/* Company Name — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name (Optional)"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
              </Grid>

              {/* Distributor Type — OPTIONAL (has default) */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Distributor Type (Optional)</InputLabel>
                  <Select
                    value={formData.distributor_type}
                    onChange={(e) => handleChange('distributor_type', e.target.value)}
                    label="Distributor Type (Optional)"
                  >
                    <MenuItem value="distributor">Distributor</MenuItem>
                    <MenuItem value="manufacturer">Manufacturer</MenuItem>
                    <MenuItem value="wholesaler">Wholesaler</MenuItem>
                    <MenuItem value="importer">Importer</MenuItem>
                    <MenuItem value="local_vendor">Local Vendor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Distributor Code — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Distributor Code (Optional)"
                  value={formData.distributor_code}
                  onChange={(e) => handleChange('distributor_code', e.target.value)}
                  placeholder="Leave blank for auto-generation"
                />
              </Grid>

            </Grid>
          </Paper>

          {/* ── Contact Information ───────────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>

              {/* Phone — MANDATORY */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  // required
                  label={<>Phone <Req /></>}
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange('phone', e.target.value)}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  error={formData.phone.length > 0 && formData.phone.length !== 10}
                  helperText={
                    formData.phone.length > 0 && formData.phone.length !== 10
                      ? 'Must be exactly 10 digits'
                      : ''
                  }
                />
              </Grid>

              {/* Alternate Phone — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alternate Phone (Optional)"
                  value={formData.alternate_phone}
                  onChange={(e) => handlePhoneChange('alternate_phone', e.target.value)}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  error={formData.alternate_phone.length > 0 && formData.alternate_phone.length !== 10}
                  helperText={
                    formData.alternate_phone.length > 0 && formData.alternate_phone.length !== 10
                      ? 'Must be exactly 10 digits'
                      : ''
                  }
                />
              </Grid>

              {/* Email — MANDATORY */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  // required
                  type="email"
                  label={<>Email <Req /></>}
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={formData.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
                  helperText={
                    formData.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                      ? 'Enter a valid email address'
                      : ''
                  }
                />
              </Grid>

              {/* Website — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website (Optional)"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                />
              </Grid>

            </Grid>
          </Paper>

          {/* ── Business Address — OPTIONAL ───────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Business Address
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — fill if known
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address (Optional)"
                  value={formData.address.street}
                  onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  value={formData.address.city}
                  onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State (Optional)"
                  value={formData.address.state}
                  onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pincode (Optional)"
                  inputProps={{ maxLength: 6 }}
                  value={formData.address.pincode}
                  onChange={(e) => handleNestedChange('address', 'pincode', e.target.value.replace(/\D/g, ''))}
                  error={formData.address.pincode !== '' && formData.address.pincode.length !== 6}
                  helperText={
                    formData.address.pincode !== '' && formData.address.pincode.length !== 6
                      ? 'Pincode must be 6 digits'
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country (Optional)"
                  value={formData.address.country}
                  onChange={(e) => handleNestedChange('address', 'country', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Legal & Compliance ────────────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Legal & Compliance
            </Typography>
            <Grid container spacing={2}>

              {/* GST — MANDATORY */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  // required
                  label={<>GST Number <Req /></>}
                  value={formData.gst_number}
                  onChange={(e) => handleChange('gst_number', e.target.value)}
                />
              </Grid>

              {/* PAN — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PAN Number (Optional)"
                  value={formData.pan_number}
                  onChange={(e) => handleChange('pan_number', e.target.value)}
                />
              </Grid>

              {/* Drug License — MANDATORY */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  // required
                  label={<>Drug License Number <Req /></>}
                  value={formData.drug_license_number}
                  onChange={(e) => handleChange('drug_license_number', e.target.value)}
                />
              </Grid>

              {/* Drug License Expiry — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Drug License Expiry (Optional)"
                  type="date"
                  value={formData.drug_license_expiry}
                  inputProps={{ min: today }}
                  onChange={(e) => {
                    if (e.target.value >= today) handleChange('drug_license_expiry', e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* FSSAI — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="FSSAI Number (Optional)"
                  value={formData.fssai_number}
                  onChange={(e) => handleChange('fssai_number', e.target.value)}
                />
              </Grid>

            </Grid>
          </Paper>

          {/* ── Banking Details — ALL OPTIONAL ───────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Banking Details
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              All fields optional — fill if payment via bank transfer
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name (Optional)"
                  value={formData.bank_details.bank_name}
                  onChange={(e) => handleNestedChange('bank_details', 'bank_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name (Optional)"
                  value={formData.bank_details.account_name}
                  onChange={(e) => handleNestedChange('bank_details', 'account_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number (Optional)"
                  value={formData.bank_details.account_number}
                  onChange={(e) => handleNestedChange('bank_details', 'account_number', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IFSC Code (Optional)"
                  value={formData.bank_details.ifsc_code}
                  onChange={(e) => handleNestedChange('bank_details', 'ifsc_code', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Branch (Optional)"
                  value={formData.bank_details.branch}
                  onChange={(e) => handleNestedChange('bank_details', 'branch', e.target.value)}
                />
              </Grid>
              {/* UPI — OPTIONAL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="UPI ID (Optional)"
                  value={formData.bank_details.upi_id}
                  onChange={(e) => handleNestedChange('bank_details', 'upi_id', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Delivery Metrics — OPTIONAL ───────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Delivery Metrics
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — can be updated later as orders are placed
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="On Time %"
                  value={formData.delivery_metrics.on_time_percentage}
                  onChange={(e) => handleNestedChange('delivery_metrics', 'on_time_percentage', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="Avg Delay Days"
                  value={formData.delivery_metrics.avg_delay_days}
                  onChange={(e) => handleNestedChange('delivery_metrics', 'avg_delay_days', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="Total Deliveries"
                  value={formData.delivery_metrics.total_deliveries}
                  onChange={(e) => handleNestedChange('delivery_metrics', 'total_deliveries', parseInt(e.target.value))}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Distributor Rating — OPTIONAL ────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Distributor Rating
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — auto-calculated from orders over time
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="Average Rating"
                  value={formData.rating.average}
                  onChange={(e) => handleNestedChange('rating', 'average', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="Total Reviews"
                  value={formData.rating.total_reviews}
                  onChange={(e) => handleNestedChange('rating', 'total_reviews', parseInt(e.target.value))}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Shipping Configuration — OPTIONAL ────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Shipping Configuration
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — configure delivery cost structure
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="number" label="Per KM Cost"
                  value={formData.shipping.per_km_cost}
                  onChange={(e) => handleNestedChange('shipping', 'per_km_cost', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="number" label="Base Cost"
                  value={formData.shipping.base_cost}
                  onChange={(e) => handleNestedChange('shipping', 'base_cost', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="number" label="Free Above ₹"
                  value={formData.shipping.free_shipping_above}
                  onChange={(e) => handleNestedChange('shipping', 'free_shipping_above', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="number" label="Avg Delivery Days"
                  value={formData.shipping.avg_delivery_days}
                  onChange={(e) => handleNestedChange('shipping', 'avg_delivery_days', parseInt(e.target.value))}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Medicines Supplied — OPTIONAL ────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Medicines Supplied
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — add medicines this distributor supplies
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Autocomplete
                freeSolo
                options={medicineSuggestions || []}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.name} (${option.medicine_id})`
                }
                loading={loadingMedicines}
                value={null}
                inputValue={medicineSearchQuery}
                onInputChange={(event, newValue) => {
                  setMedicineSearchQuery(newValue);
                  if (newValue && newValue.length >= 2) fetchMedicineSuggestions(newValue);
                  else setMedicineSuggestions([]);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    const exists = medicineList.some(m => m.medicine_id === newValue.medicine_id);
                    if (exists) { showSnackbar('Medicine already added', 'warning'); setMedicineSearchQuery(''); return; }
                    setMedicineList(prev => [...prev, {
                      medicine_id: newValue.medicine_id,
                      medicine_name: newValue.name,
                      price: newValue.price || 0,
                      discounted_price: newValue.discounted_price || newValue.price || 0,
                      discount_percentage: 0,
                      is_new: false,
                    }]);
                    setMedicineSearchQuery('');
                    setMedicineSuggestions([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search Medicine or Add New"
                    placeholder="Type medicine name to search..."
                    helperText="Select from suggestions or press Enter to add new"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && medicineSearchQuery.trim()) {
                        e.preventDefault();
                        if (medicineSuggestions.length === 0) {
                          const exists = medicineList.some(
                            m => m.medicine_name.toLowerCase() === medicineSearchQuery.trim().toLowerCase()
                          );
                          if (exists) { showSnackbar('Medicine already added', 'warning'); setMedicineSearchQuery(''); return; }
                          setMedicineList(prev => [...prev, {
                            medicine_id: `NEW-${Date.now()}`,
                            medicine_name: medicineSearchQuery.trim(),
                            price: 0, discounted_price: 0, discount_percentage: 0, is_new: true,
                          }]);
                          showSnackbar('New medicine added', 'success');
                          setMedicineSearchQuery('');
                          setMedicineSuggestions([]);
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>{loadingMedicines ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.medicine_id}>
                    <Box sx={{ width: '100%', py: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.medicine_id} | MRP: ₹{option.price || 0} | Stock: {option.stock || 0}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText={
                  medicineSearchQuery.length < 2
                    ? 'Type at least 2 characters to search'
                    : `No matches. Press Enter to add "${medicineSearchQuery}" as new`
                }
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {medicineList.length > 0 ? (
              <Box>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  Selected Medicines ({medicineList.length})
                </Typography>
                {medicineList.map((med, index) => (
                  <Card
                    key={index}
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      bgcolor: med.is_new ? '#FFF4E6' : '#F0F9FF',
                      border: '1px solid',
                      borderColor: med.is_new ? '#FB923C' : '#93C5FD',
                      borderStyle: med.is_new ? 'dashed' : 'solid',
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {med.medicine_name || 'Unknown Medicine'}
                          </Typography>
                          {med.is_new
                            ? <Chip label="NEW" size="small" sx={{ height: 20, bgcolor: '#FB923C', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                            : <Chip label={med.medicine_id} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'monospace', borderColor: '#3B82F6', color: '#3B82F6' }} />
                          }
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveMedicine(index)} sx={{ width: 28, height: 28, color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'white' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      {med.is_new && (
                        <Box sx={{ mb: 1.5 }}>
                          <TextField
                            fullWidth size="small" label="Medicine ID (Optional)"
                            value={med.medicine_id.startsWith('NEW-') ? '' : med.medicine_id}
                            onChange={(e) => handleMedicineChange(index, 'medicine_id', e.target.value || `NEW-${Date.now()}`)}
                            placeholder="Auto-generated if left blank"
                            sx={{ '& .MuiInputBase-root': { height: 36 } }}
                          />
                        </Box>
                      )}
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth type="number" label="MRP" size="small"
                            value={med.price || 0}
                            onChange={(e) => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              handleMedicineChange(index, 'price', newPrice);
                              if (med.discounted_price === med.price || !med.discounted_price)
                                handleMedicineChange(index, 'discounted_price', newPrice);
                            }}
                            InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography> }}
                            sx={{ '& .MuiInputBase-root': { height: 36 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth type="number" label="Selling Price" size="small"
                            value={med.discounted_price || 0}
                            onChange={(e) => {
                              const sp = parseFloat(e.target.value) || 0;
                              handleMedicineChange(index, 'discounted_price', sp);
                              if (med.price > 0)
                                handleMedicineChange(index, 'discount_percentage', Math.max(0, Math.round(((med.price - sp) / med.price) * 10000) / 100));
                            }}
                            InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography> }}
                            error={med.price > 0 && med.discounted_price > med.price}
                            sx={{ '& .MuiInputBase-root': { height: 36 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth type="number" label="Discount" size="small"
                            value={med.discount_percentage || 0}
                            onChange={(e) => {
                              const disc = parseFloat(e.target.value) || 0;
                              handleMedicineChange(index, 'discount_percentage', disc);
                              if (med.price > 0)
                                handleMedicineChange(index, 'discounted_price', Math.max(0, Math.round((med.price - (med.price * disc) / 100) * 100) / 100));
                            }}
                            InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>%</Typography> }}
                            sx={{ '& .MuiInputBase-root': { height: 36 } }}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 1.5, bgcolor: med.is_new ? '#FFEDD5' : '#DBEAFE', borderRadius: 1, p: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">MRP</Typography>
                            <Typography variant="body2" fontWeight={700}>₹{(med.price || 0).toFixed(2)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Selling</Typography>
                            <Typography variant="body2" fontWeight={700} color="primary.main">₹{(med.discounted_price || 0).toFixed(2)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">You Save</Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              ₹{((med.price || 0) - (med.discounted_price || 0)).toFixed(2)}
                              {med.price > 0 && <Typography component="span" variant="caption" sx={{ ml: 0.3, fontWeight: 600 }}>({(med.discount_percentage || 0).toFixed(1)}%)</Typography>}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Alert severity="info" icon={<Add />} sx={{ py: 1, bgcolor: '#E0F2FE', border: '1px solid #BAE6FD' }}>
                <Typography variant="body2" fontWeight={500}>No medicines added yet</Typography>
                <Typography variant="caption" color="text.secondary">
                  Search and select or type a new medicine name and press Enter
                </Typography>
              </Alert>
            )}
          </Paper>

          {/* ── Geo Location — OPTIONAL ───────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Geo Location
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — used for distance-based delivery calculation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Latitude (Optional)"
                  value={formData.location.lat}
                  onChange={(e) => { handleNestedChange('location', 'lat', e.target.value); validateField('lat', e.target.value); }}
                  error={!!errors.lat} helperText={errors.lat}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Longitude (Optional)"
                  value={formData.location.lng}
                  onChange={(e) => { handleNestedChange('location', 'lng', e.target.value); validateField('lng', e.target.value); }}
                  error={!!errors.lng} helperText={errors.lng}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="City (Optional)" value={formData.location.city}
                  onChange={(e) => handleNestedChange('location', 'city', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="State (Optional)" value={formData.location.state}
                  onChange={(e) => handleNestedChange('location', 'state', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Pincode (Optional)" inputProps={{ maxLength: 6 }}
                  value={formData.location.pincode}
                  onChange={(e) => handleNestedChange('location', 'pincode', e.target.value.replace(/\D/g, ''))} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Business Terms — OPTIONAL (have defaults) ────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Business Terms
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — defaults are pre-filled, update as needed
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select value={formData.payment_terms}
                    onChange={(e) => handleChange('payment_terms', e.target.value)}
                    label="Payment Terms">
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit_7_days">Credit 7 Days</MenuItem>
                    <MenuItem value="credit_15_days">Credit 15 Days</MenuItem>
                    <MenuItem value="credit_30_days">Credit 30 Days</MenuItem>
                    <MenuItem value="credit_45_days">Credit 45 Days</MenuItem>
                    <MenuItem value="credit_60_days">Credit 60 Days</MenuItem>
                    <MenuItem value="advance_payment">Advance Payment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Credit Limit (₹)"
                  value={formData.credit_limit}
                  onChange={(e) => handleChange('credit_limit', parseFloat(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Credit Period (Days)"
                  value={formData.credit_period_days}
                  onChange={(e) => handleChange('credit_period_days', parseInt(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Discount Percentage (%)"
                  value={formData.discount_percentage}
                  onChange={(e) => handleChange('discount_percentage', parseFloat(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Minimum Order Value (₹)"
                  value={formData.minimum_order_value}
                  onChange={(e) => handleChange('minimum_order_value', parseFloat(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Delivery Days"
                  value={formData.delivery_days}
                  onChange={(e) => handleChange('delivery_days', parseInt(e.target.value) || 0)} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Contact Persons — OPTIONAL ────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Contact Persons</Typography>
              <Button startIcon={<Add />} onClick={handleAddContactPerson} variant="outlined" size="small">
                Add Contact
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Optional — add point-of-contact people for this distributor
            </Typography>
            {contactPersons.map((contact, index) => (
              <Card key={index} sx={{ mb: 2, bgcolor: '#f9f9f9' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1, fontSize: 20 }} />
                      Contact Person {index + 1}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveContactPerson(index)}>
                      <Delete />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" label="Name" value={contact.name}
                        onChange={(e) => handleContactPersonChange(index, 'name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" label="Designation" value={contact.designation}
                        onChange={(e) => handleContactPersonChange(index, 'designation', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" label="Phone" value={contact.phone}
                        onChange={(e) => {
                          let v = e.target.value.replace('+91', '').replace(/\D/g, '').slice(0, 10);
                          handleContactPersonChange(index, 'phone', v);
                        }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" type="email" label="Email" value={contact.email || ''}
                        onChange={(e) => handleContactPersonChange(index, 'email', e.target.value)}
                        error={contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)}
                        helperText={contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) ? 'Enter a valid email' : ''} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={contact.is_primary}
                          onChange={(e) => handleContactPersonChange(index, 'is_primary', e.target.checked)} />}
                        label="Primary Contact"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            {contactPersons.length === 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                No contact persons added. Click "Add Contact" to add one.
              </Alert>
            )}
          </Paper>

          {/* ── Product Categories — OPTIONAL ─────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>Product Categories</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Optional</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField fullWidth size="small" label="Add Category" value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()} />
              <Button variant="outlined" onClick={handleAddCategory}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.product_categories.map((cat, i) => (
                <Chip key={i} label={cat} onDelete={() => handleRemoveCategory(cat)} color="primary" variant="outlined" />
              ))}
            </Box>
          </Paper>

          {/* ── Brands Supplied — OPTIONAL ───────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>Brands Supplied</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Optional</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField fullWidth size="small" label="Add Brand" value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddBrand()} />
              <Button variant="outlined" onClick={handleAddBrand}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.brands_supplied.map((brand, i) => (
                <Chip key={i} label={brand} onDelete={() => handleRemoveBrand(brand)} color="secondary" variant="outlined" />
              ))}
            </Box>
          </Paper>

          {/* ── Additional Info — OPTIONAL ───────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>Additional Information</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Optional</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Notes" value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Terms & Conditions" value={formData.terms_conditions}
                  onChange={(e) => handleChange('terms_conditions', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Status ───────────────────────────────────────────────────── */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Status</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)} color="success" />}
                  label="Active"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={formData.is_verified}
                    onChange={(e) => handleChange('is_verified', e.target.checked)} color="info" />}
                  label="Verified"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Mandatory summary reminder ────────────────────────────────── */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600}>Before saving, please confirm:</Typography>
            <Typography variant="body2">
              ✅ Distributor Name &nbsp;|&nbsp; ✅ GST Number &nbsp;|&nbsp;
              ✅ Drug License Number &nbsp;|&nbsp; ✅ Phone &nbsp;|&nbsp; ✅ Email
            </Typography>
          </Alert>

          {/* ── Submit Buttons ────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              sx={{ borderColor: '#006D77', color: '#006D77' }}
              onClick={() => navigate('/DistributorList')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{ bgcolor: '#006D77', textTransform: 'none', '&:hover': { bgcolor: '#005962' } }}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'} Distributor
            </Button>
          </Box>
        </form>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DistributorForm;